import { Config } from "@planetscale/database";
import { select } from "gamedata/GameData";
import { GD_ALL } from "~/gd";
import { $LOADER, $PAGE, OurLoaderArgs } from "~/util/Loaders";

import css from "./locs.module.css";
import { Link } from "react-router";
import { Realm, lstring, useT, useTurl } from "~/util/Lang";
import React from "react";
import { homePage } from "~/util/titles";
import { gameName } from "~/util/common";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { logPageView } from "~/util/stats";

type Loc = {
  locName: string;
  name: { en: string };
  type?: "town" | "harbor" | "castle";
  levels: { min: number | null; max: number | null };
};

async function makeData(la: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(la);
  //
  const all = Array.from(GD_ALL[la.realm.game].locs.values());

  const locs = select(all, {
    select: {
      territory: { areaName: true, name: { en: true } },
      locName: true,
      name: { en: true },
      type: true,
      levels: { min: true, max: true },
    },
  });

  return { locs, authUser: la.req.sess?.user };
}

$PAGE({
  path: "locs",

  loader: $LOADER?.(async (la) => {
    return makeData(la, {});
  }),

  meta: (args) => {
    return {
      title: `${args.t({ en: "Locations", ru: "Локации" })} - ${homePage(
        args.realm
      )}`,
      description: getDesc(args),
      ogDescription: getDesc(args),
      ogTitle: getOgTitle(args),
    };
  },

  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const locs = reduceLocs(data.locs);
    const locsTitle = t({ en: "Locations", ru: "Локации" });

    return (
      <div className="content">
        <Breadcrumb items={[{ title: locsTitle, url: turl("/locs") }]} />
        <PageTitle>{locsTitle}</PageTitle>

        <div className={css.locs}>
          <section className={css.block}>
            {locs.map((zone, i) => (
              <section key={i} className={css.groupWrapper}>
                <section className={css.group}>
                  <section className={css.groupName}>{zone.name.en}</section>
                  <SignificantLocations locs={zone.locs} />
                  <section className={css.groupWrapper}>
                    <Locations locs={zone.locs} />
                  </section>
                </section>
              </section>
            ))}
          </section>
        </div>
      </div>
    );
  },
});

function SignificantLocations(props: {
  locs: {
    locName: string;
    name: { en: string };
    type?: "town" | "harbor" | "castle";
  }[];
}) {
  const turl = useTurl();
  const town = props.locs.filter((loc) => loc.type === "town").at(0);
  const castle = props.locs.filter((loc) => loc.type === "castle").at(0);
  const harbor = props.locs.filter((loc) => loc.type === "harbor").at(0);

  return (
    <section>
      {town && (
        <section>
          <Link to={turl("/locs/" + town.locName)}>{town.name.en}</Link>
        </section>
      )}
      {castle && (
        <section>
          <Link to={turl("/locs/" + castle.locName)}>{castle.name.en}</Link>
        </section>
      )}
      {harbor && (
        <section>
          <Link to={turl("/locs/" + harbor.locName)}>{harbor.name.en}</Link>
        </section>
      )}
    </section>
  );
}

function Locations(props: { locs: Loc[] }) {
  const turl = useTurl();

  return (
    <section>
      <section className={css.lineGroup}>
        <section></section>
        <section
          style={{
            textTransform: "uppercase",
            fontSize: 12,
          }}
        >
          Location
        </section>

        {props.locs
          .filter((loc) => !loc.type)
          .map((loc, i) => (
            <React.Fragment key={i}>
              <section className={css.lineEnding}>
                {loc.levels.min ? loc.levels.min + "-" + loc.levels.max : null}
              </section>
              <section>
                <Link to={turl("/locs/" + loc.locName)}>{loc.name.en}</Link>
              </section>
            </React.Fragment>
          ))}
      </section>
    </section>
  );
}

type ReducedLoc = {
  areaName: string;
  name: { en: string };
  locs: Loc[];
};

type EnteryLoc = Loc & {
  territory: {
    areaName: string;
    name: { en: string };
  };
};

const reduceLocs = (items: EnteryLoc[]): ReducedLoc[] => {
  return Object.values(
    items.reduce((acc, item) => {
      const { areaName, name } = item.territory;
      if (!acc[areaName]) {
        acc[areaName] = { areaName, name, locs: [] };
      }
      acc[areaName].locs.push({
        locName: item.locName,
        name: item.name,
        type: item.type,
        levels: item.levels,
      });
      return acc;
    }, {} as { [key: string]: ReducedLoc })
  );
};

function getDesc(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `A complete searchable and filterable list of all Locations in ${gameName(
      cx.realm.game
    )}.`,
    ru: `Полный список локаций ${gameName(
      cx.realm.game
    )} с полной информацие по присутсвующим НПЦ, по дропу, по спойлу, по торговцам и по квестам.`,
  });
}

function getOgTitle(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `All locations ${gameName(cx.realm.game)}`,
    ru: `Все локации ${gameName(cx.realm.game)}`,
  });
}
